import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Loader2, User, Minimize2, Maximize2, Mic, MicOff, Star, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChatbotWidget() {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('ct_chatbot_session') || '');
  const [isRecording, setIsRecording] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [rated, setRated] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (open && !minimized && sessionId) {
      axios.get(`${API}/api/chatbot/history/${sessionId}`)
        .then(r => setMessages(r.data?.messages || []))
        .catch(() => {});
    }
  }, [open, minimized, sessionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/chatbot/message`, {
        message: userMsg,
        session_id: sessionId || undefined,
      });
      const newSessionId = res.data.session_id;
      if (!sessionId) {
        setSessionId(newSessionId);
        localStorage.setItem('ct_chatbot_session', newSessionId);
      }
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Ispri\u010davam se, ne\u0161to je po\u0161lo po krivu. Poku\u0161ajte ponovno.' }]);
    }
    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startNewChat = () => {
    setMessages([]);
    setSessionId('');
    setRated(false);
    setEscalated(false);
    setShowRating(false);
    localStorage.removeItem('ct_chatbot_session');
  };

  const toggleVoice = () => {
    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const recognition = new SR();
    recognition.lang = 'hr-HR';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setInput(prev => prev + transcript);
      setIsRecording(false);
    };
    recognition.onerror = () => setIsRecording(false);
    recognition.onend = () => setIsRecording(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
  };

  const handleRate = async (rating) => {
    if (!sessionId) return;
    try {
      await axios.post(`${API}/api/chatbot/rate`, { session_id: sessionId, rating });
      setRated(true);
    } catch {}
  };

  const handleEscalate = async () => {
    if (!sessionId) return;
    try {
      const res = await axios.post(`${API}/api/chatbot/escalate`, { session_id: sessionId });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.message || 'Razgovor je proslijeđen timu podrške.' }]);
      setEscalated(true);
    } catch {}
  };

  return (
    <>
      {/* Floating Button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chatbot-toggle"
          className="fixed bottom-6 right-6 z-[9999] w-14 h-14 bg-gradient-to-r from-sky-500 to-indigo-500 text-white rounded-full shadow-xl shadow-sky-500/25 flex items-center justify-center hover:scale-105 transition-transform"
          aria-label="Open chat support"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Minimized Bar */}
      {open && minimized && (
        <div
          className="fixed bottom-6 right-6 z-[9999] bg-gradient-to-r from-sky-500 to-indigo-500 text-white rounded-full shadow-xl px-4 py-2 flex items-center gap-3 cursor-pointer hover:scale-105 transition-transform"
          onClick={() => setMinimized(false)}
          data-testid="chatbot-minimized"
        >
          <MessageCircle size={16} />
          <span className="text-sm font-semibold">Pomo\u0107nik portala</span>
          <Maximize2 size={14} />
        </div>
      )}

      {/* Chat Panel */}
      {open && !minimized && (
        <div className="fixed bottom-6 right-6 z-[9999] w-[380px] max-w-[calc(100vw-32px)] h-[520px] max-h-[calc(100vh-80px)] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden animate-fade-in" data-testid="chatbot-panel">
          {/* Header */}
          <div className="bg-gradient-to-r from-sky-500 to-indigo-500 text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle size={14} />
              </div>
              <div>
                <h3 className="font-display text-sm font-semibold">Pomo\u0107nik portala</h3>
                <span className="text-[10px] text-white/70">Online</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={startNewChat} className="p-1.5 hover:bg-white/10 rounded-lg text-[10px] font-medium transition-colors" data-testid="chatbot-new-chat">Novi</button>
              <button onClick={() => setMinimized(true)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="chatbot-minimize" title="Minimiziraj">
                <Minimize2 size={14} />
              </button>
              <button onClick={() => { setOpen(false); setMinimized(false); }} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="chatbot-close">
                <X size={16} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="chatbot-messages">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-sky-100 dark:bg-sky-900/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <MessageCircle size={24} className="text-sky-500" />
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 font-display font-medium">Kako vam mogu pomo\u0107i?</p>
                <p className="text-xs text-gray-400 mt-1">Pitajte me o Croatia Times portalu</p>
                <div className="flex flex-wrap gap-1.5 justify-center mt-3">
                  {['Pretplata info', 'Times Veze', 'Times Majstor', 'Najnovije vijesti'].map(q => (
                    <button
                      key={q}
                      onClick={() => setInput(q)}
                      className="px-2.5 py-1 bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 text-[11px] rounded-full hover:bg-sky-100 dark:hover:bg-sky-500/20 transition-colors font-display"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-6 h-6 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MessageCircle size={10} className="text-sky-500" />
                  </div>
                )}
                <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-[13px] leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-sky-500 text-white rounded-br-md'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-bl-md'
                }`}>
                  {msg.content}
                </div>
                {msg.role === 'user' && (
                  <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User size={10} className="text-gray-500" />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-2 items-center">
                <div className="w-6 h-6 rounded-full bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                  <MessageCircle size={10} className="text-sky-500" />
                </div>
                <div className="bg-gray-100 dark:bg-gray-800 px-3 py-2 rounded-2xl rounded-bl-md">
                  <Loader2 size={14} className="animate-spin text-sky-500" />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Action buttons (escalate, rate) */}
          {messages.length > 2 && (
            <div className="px-3 pb-1 flex items-center gap-2 flex-shrink-0">
              {!escalated && (
                <button onClick={handleEscalate} data-testid="chatbot-escalate" className="flex items-center gap-1 px-2 py-1 text-[10px] bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-full hover:bg-amber-100 transition-colors">
                  <AlertTriangle size={10} /> Razgovor s osobom
                </button>
              )}
              {!showRating && !rated && (
                <button onClick={() => setShowRating(true)} data-testid="chatbot-show-rating" className="flex items-center gap-1 px-2 py-1 text-[10px] bg-gray-50 dark:bg-gray-800 text-gray-500 rounded-full hover:bg-gray-100 transition-colors">
                  <Star size={10} /> Ocijeni
                </button>
              )}
              {showRating && !rated && (
                <div className="flex items-center gap-0.5">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} onClick={() => handleRate(n)} data-testid={`chatbot-rate-${n}`} className="p-1 hover:scale-125 transition-transform">
                      <Star size={14} className="text-amber-400" fill={n <= 3 ? 'none' : 'currentColor'} />
                    </button>
                  ))}
                </div>
              )}
              {rated && <span className="text-[10px] text-green-500">Hvala na ocjeni!</span>}
            </div>
          )}

          {/* Input */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-3 flex-shrink-0">
            <div className="flex items-center gap-2">
              <button
                onClick={toggleVoice}
                data-testid="chatbot-voice"
                className={`p-2 rounded-xl transition-colors flex-shrink-0 ${isRecording ? 'bg-red-100 text-red-500 animate-pulse' : 'bg-gray-100 dark:bg-gray-800 text-gray-400 hover:text-sky-500'}`}
              >
                {isRecording ? <MicOff size={16} /> : <Mic size={16} />}
              </button>
              <input
                type="text"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Napi\u0161ite poruku..."
                data-testid="chatbot-input"
                className="flex-1 px-3 py-2 rounded-xl text-sm border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 font-display"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || loading}
                data-testid="chatbot-send"
                className="p-2 bg-sky-500 text-white rounded-xl hover:bg-sky-600 disabled:opacity-40 transition-colors flex-shrink-0"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
